import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ArrowRight, Clock3, FileText, History as HistoryIcon, Layers, ListChecks, Play, RefreshCw, Trash2, ClipboardPlus, CopyCheck } from "lucide-react";
import type { AnnanRole, Perspective } from "@samklang/schemas";
import { Button, Card, Input } from "@samklang/ui";
import { useKartlaggningStore, type PerspectiveRun } from "@/stores/kartlaggning-store";
import { ANNAN_ROLES, formatDate, formatDateTime, perspectiveLabel, perspectiveShortLabel, runStatusLabel } from "./lib";

interface DetailProps {
  readonly kartlaggningId: string;
}

export function KartlaggningDetailScreen({ kartlaggningId }: DetailProps) {
  const navigate = useNavigate();
  const kartlaggning = useKartlaggningStore((store) => store.getKartlaggning(kartlaggningId));
  const all = useKartlaggningStore((store) => store.listKartlaggningar)();
  const startRun = useKartlaggningStore((store) => store.startRun);
  const setRunProvenance = useKartlaggningStore((store) => store.setRunProvenance);
  const deleteKartlaggning = useKartlaggningStore((store) => store.deleteKartlaggning);
  const reusePreviousKartlaggning = useKartlaggningStore((store) => store.reusePreviousKartlaggning);
  const [editing, setEditing] = useState<Perspective | undefined>(undefined);
  const [provenance, setProvenance] = useState<{ conductedBy: string; conductedWith: string; registeredBy: string }>({ conductedBy: "", conductedWith: "", registeredBy: "" });
  const [role, setRole] = useState<AnnanRole>("specialpedagog");
  const [showReuse, setShowReuse] = useState(false);

  if (!kartlaggning) {
    return (
      <section className="mx-auto max-w-2xl px-5 py-10">
        <Card>
          <h1 className="font-display text-2xl font-semibold">Kartläggningen finns inte.</h1>
          <Button variant="ghost" className="mt-4" onClick={() => void navigate({ to: "/kartlaggningar" })}>Tillbaka till listan</Button>
        </Card>
      </section>
    );
  }

  const gotoRun = (perspective: Perspective) => {
    try {
      startRun(kartlaggningId, perspective);
    } catch {
      /* already in progress or done; still allowed to open run screen for in-progress */
    }
    void navigate({ to: "/kartlaggningar/$kartlaggningId/run/$perspective", params: { kartlaggningId, perspective } });
  };

  const openEdit = (run: PerspectiveRun) => {
    setEditing(run.perspective);
    setProvenance({ conductedBy: run.conductedBy, conductedWith: run.conductedWith, registeredBy: run.registeredBy });
    if (run.perspective === "annan") setRole(run.role ?? "specialpedagog");
  };

  const saveProvenance = (perspective: Perspective) => {
    if (!provenance.registeredBy.trim()) return;
    const chosenRole = perspective === "annan" ? role : undefined;
    setRunProvenance(kartlaggningId, perspective, {
      conductedBy: provenance.conductedBy.trim(),
      conductedWith: provenance.conductedWith.trim(),
      registeredBy: provenance.registeredBy.trim(),
      ...(chosenRole ? { role: chosenRole } : {}),
    });
    setEditing(undefined);
  };

  const remove = () => {
    if (!window.confirm("Vill du radera hela kartläggningen permanent? Alla svar och insatser försvinner.")) return;
    deleteKartlaggning(kartlaggningId);
    void navigate({ to: "/kartlaggningar" });
  };

  const reuseSources = all.filter(
    (candidate) => candidate.id !== kartlaggningId && candidate.participantReference === kartlaggning.participantReference && candidate.runs.some((run) => run.status === "done"),
  );

  return (
    <section className="mx-auto w-full max-w-4xl px-5 py-8 sm:px-8">
      <div className="flex items-start justify-between gap-4">
        <div>
          <button onClick={() => void navigate({ to: "/kartlaggningar" })} className="text-sm font-semibold text-primary underline">← Kartläggningar</button>
          <h1 className="mt-2 font-display text-4xl font-semibold tracking-tight">{kartlaggning.title}</h1>
          <p className="mt-1 text-foreground-muted">
            Lokalt id: <span className="font-semibold text-foreground">{kartlaggning.participantReference || "—"}</span> · Skapad av {kartlaggning.createdBy || "—"} · {formatDate(kartlaggning.createdAt)}
          </p>
        </div>
        <Button variant="ghost" aria-label="Radera kartläggning" onClick={remove}>
          <Trash2 size={19} />
        </Button>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button onClick={() => void navigate({ to: "/kartlaggningar/$kartlaggningId/sammanstallning", params: { kartlaggningId } })}>
          <Layers size={19} /> Sammanställ
        </Button>
        <Button variant="secondary" onClick={() => void navigate({ to: "/kartlaggningar/$kartlaggningId/usk", params: { kartlaggningId } })}>
          <FileText size={19} /> USK-underlag
        </Button>
        <Button variant="outline" onClick={() => void navigate({ to: "/kartlaggningar/$kartlaggningId/uppfoljning", params: { kartlaggningId } })}>
          <RefreshCw size={19} /> Följ upp
        </Button>
        <Button variant="outline" onClick={() => void navigate({ to: "/kartlaggningar/$kartlaggningId/insatser", params: { kartlaggningId } })}>
          <ListChecks size={19} /> Insatser
        </Button>
        <Button variant="outline" onClick={() => void navigate({ to: "/kartlaggningar/$kartlaggningId/utveckling", params: { kartlaggningId } })}>
          <HistoryIcon size={19} /> Utveckling över tid
        </Button>
        <Button variant="outline" onClick={() => void navigate({ to: "/kartlaggningar/$kartlaggningId/pdf", params: { kartlaggningId } })}>
          <ClipboardPlus size={19} /> PDF
        </Button>
        <Button variant="outline" onClick={() => setShowReuse((current) => !current)} disabled={reuseSources.length === 0}>
          <CopyCheck size={19} /> Återanvänd tidigare underlag
        </Button>
      </div>

      {showReuse ? (
        <Card className="mt-4">
          <h2 className="font-display text-lg font-semibold">Återanvänd tidigare underlag</h2>
          <p className="mt-1 text-sm text-foreground-muted">Kopiera genomförda svar från en tidigare kartläggning för samma deltagare. Befintliga svar för respektive perspektiv skrivs inte över.</p>
          <ul className="mt-4 space-y-2">
            {reuseSources.length === 0 ? <li className="text-sm text-foreground-muted">Inga tidigare kartläggningar för denna deltagare med genomförda perspektiv.</li> : null}
            {reuseSources.map((source) => {
              const copyable = source.runs.filter((run) => run.status === "done").map((run) => perspectiveShortLabel(run.perspective, run.role));
              return (
                <li key={source.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border p-3">
                  <div>
                    <p className="font-semibold">{source.title}</p>
                    <p className="text-sm text-foreground-muted">{formatDate(source.createdAt)} · Kopiering: {copyable.join(", ")}</p>
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      reusePreviousKartlaggning(kartlaggningId, source.id);
                      setShowReuse(false);
                    }}
                  >
                    <CopyCheck size={18} /> Återanvänd
                  </Button>
                </li>
              );
            })}
          </ul>
        </Card>
      ) : null}

      <div className="mt-8 grid gap-4">
        {kartlaggning.runs.map((run) => (
          <Card key={run.perspective} padding="md">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="font-display text-xl font-semibold">{perspectiveLabel(run.perspective, run.role)}</h2>
                  <span className="rounded-full bg-surface-muted px-3 py-1 text-sm font-medium text-foreground-muted">{runStatusLabel(run.status)}</span>
                </div>
                <div className="mt-3 grid gap-1 text-sm text-foreground-muted min-[480px]:grid-cols-2">
                  <span>Ansvara för genomförande: <strong className="text-foreground">{run.conductedBy || "—"}</strong></span>
                  <span>Genomfört tillsammans med: <strong className="text-foreground">{run.conductedWith || "—"}</strong></span>
                  <span>Registrerad av: <strong className="text-foreground">{run.registeredBy || "—"}</strong></span>
                  <span>Formversion: <strong className="text-foreground">{run.formVersion}</strong></span>
                  <span>Påbörjad: {formatDateTime(run.startedAt)}</span>
                  <span>Avslutad: {formatDateTime(run.finishedAt)}</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {run.status !== "done" ? (
                  <Button onClick={() => gotoRun(run.perspective)}>
                    <Play size={18} /> {run.status === "in_progress" ? "Fortsätt" : "Genomför"}
                  </Button>
                ) : (
                  <Button variant="secondary" onClick={() => void navigate({ to: "/kartlaggningar/$kartlaggningId/run/$perspective", params: { kartlaggningId, perspective: run.perspective } })}>
                    <ArrowRight size={18} /> Visa svar
                  </Button>
                )}
                <Button variant="ghost" onClick={() => openEdit(run)}>Redigera närvaro</Button>
              </div>
            </div>

            {editing === run.perspective ? (
              <div className="mt-5 border-t border-border/60 pt-5">
                <p className="mb-3 text-sm font-semibold">Närvarokänslighet · {perspectiveLabel(run.perspective, run.role)}</p>
                <div className="grid gap-3 min-[480px]:grid-cols-2">
                  <label className="grid gap-1 text-sm font-semibold">
                    Genomfört av
                    <Input value={provenance.conductedBy} onChange={(event) => setProvenance((current) => ({ ...current, conductedBy: event.target.value }))} maxLength={80} />
                  </label>
                  <label className="grid gap-1 text-sm font-semibold">
                    Genomfört tillsammans med
                    <Input value={provenance.conductedWith} onChange={(event) => setProvenance((current) => ({ ...current, conductedWith: event.target.value }))} maxLength={80} />
                  </label>
                  <label className="grid gap-1 text-sm font-semibold">
                    Registrerad av
                    <Input value={provenance.registeredBy} onChange={(event) => setProvenance((current) => ({ ...current, registeredBy: event.target.value }))} maxLength={80} />
                  </label>
                  {run.perspective === "annan" ? (
                    <label className="grid gap-1 text-sm font-semibold">
                      Roll
                      <select value={role} onChange={(event) => setRole(event.target.value as AnnanRole)} className="min-h-11 rounded-xl border border-border bg-surface-raised px-3">
                        {ANNAN_ROLES.map((option) => (
                          <option key={option} value={option}>
                            {perspectiveLabel("annan", option)}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button onClick={() => saveProvenance(run.perspective)} disabled={!provenance.registeredBy.trim()}>Spara närvaro</Button>
                  <Button variant="ghost" onClick={() => setEditing(undefined)}>Avbryt</Button>
                </div>
              </div>
            ) : null}
          </Card>
        ))}
      </div>

      <div className="mt-8">
        <Card className="bg-secondary-soft/50">
          <h3 className="flex items-center gap-2 font-display text-lg font-semibold">
            <Clock3 size={19} className="text-secondary" /> Åtkomstlogg
          </h3>
          <ul className="mt-3 space-y-1.5 text-sm text-foreground-muted">
            {kartlaggning.accessLog.length === 0 ? <li>Inga händelser registrerade.</li> : null}
            {[...kartlaggning.accessLog].reverse().slice(0, 8).map((entry, index) => (
              <li key={`${entry.at}-${index}`}>
                <span className="text-foreground-subtle">{formatDateTime(entry.at)}</span> · {entry.actor || "—"} · {entry.action}: {entry.subject}
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </section>
  );
}
